import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../utils/apiClient';

interface AccessRequest {
  _id: string;
  requesterId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    profilePicture?: string;
  };
  status: 'pending' | 'approved' | 'rejected' | 'revoked';
  requestedAt: string;
  respondedAt?: string;
  respondedBy?: {
    firstName: string;
    lastName: string;
  };
  message?: string;
}

interface AccessRequestsTableProps {
  dossierId: string;
}

const statusConfig: Record<string, { label: string; className: string }> = {
  pending: {
    label: 'En attente',
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  },
  approved: {
    label: 'Approuvé',
    className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  },
  rejected: {
    label: 'Refusé',
    className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  },
  revoked: {
    label: 'Révoqué',
    className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  },
};

const AccessRequestsTable: React.FC<AccessRequestsTableProps> = ({ dossierId }) => {
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantSubmitting, setGrantSubmitting] = useState(false);
  const [grantMessage, setGrantMessage] = useState('');
  const [grantError, setGrantError] = useState('');

  const fetchRequests = useCallback(async () => {
    try {
      const response = await apiClient(`/access-requests/${dossierId}`);
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (err) {
      console.error('Failed to fetch access requests:', err);
    } finally {
      setIsLoading(false);
    }
  }, [dossierId]);

  useEffect(() => {
    if (dossierId) {
      fetchRequests();
    }
  }, [dossierId, fetchRequests]);

  const handleGrantAccess = async () => {
    if (!grantEmail.trim()) return;

    setGrantError('');
    setGrantMessage('');
    setGrantSubmitting(true);

    try {
      const response = await apiClient(`/access-requests/${dossierId}/grant`, {
        method: 'POST',
        body: JSON.stringify({ email: grantEmail.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        setGrantMessage(data.message);
        setGrantEmail('');
        fetchRequests();
        setTimeout(() => setGrantMessage(''), 4000);
      } else {
        setGrantError(data.message || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      setGrantError('Erreur réseau');
    } finally {
      setGrantSubmitting(false);
    }
  };

  const handleRespond = async (requestId: string, action: 'approve' | 'reject') => {
    try {
      const response = await apiClient(`/access-requests/${requestId}/respond`, {
        method: 'PATCH',
        body: JSON.stringify({ action }),
      });
      if (response.ok) {
        fetchRequests();
      }
    } catch (err) {
      console.error('Failed to respond to access request:', err);
    }
  };

  const handleRevoke = async (requestId: string) => {
    try {
      const response = await apiClient(`/access-requests/${requestId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        fetchRequests();
      }
    } catch (err) {
      console.error('Failed to revoke access:', err);
    }
  };

  return (
    <div className="space-y-4">
      {/* Formulaire d'ajout manuel */}
      <div>
        <div className="flex gap-2">
          <input
            type="email"
            placeholder="Accorder un accès par email..."
            value={grantEmail}
            onChange={(e) => {
              setGrantEmail(e.target.value);
              if (grantError) setGrantError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && grantEmail.trim() && !grantSubmitting) {
                handleGrantAccess();
              }
            }}
            disabled={grantSubmitting}
            className="flex-1 py-2 px-3 rounded-lg border border-mcdm-primary-200 dark:border-mcdm-third-300 bg-white dark:bg-mcdm-third-500 text-sm text-mcdm-third-500 dark:text-mcdm-primary-100 placeholder-mcdm-third-300 dark:placeholder-mcdm-primary-300 focus:ring-2 focus:ring-mcdm-secondary-400 focus:border-transparent outline-none"
          />
          <button
            onClick={handleGrantAccess}
            disabled={grantSubmitting || !grantEmail.trim()}
            className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
              grantSubmitting || !grantEmail.trim()
                ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                : 'bg-mcdm-green-400 hover:bg-mcdm-green-500'
            }`}
          >
            {grantSubmitting ? 'Envoi...' : 'Accorder'}
          </button>
        </div>

        {grantMessage && (
          <p className="text-xs text-green-600 dark:text-green-400 mt-2">{grantMessage}</p>
        )}
        {grantError && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-2">{grantError}</p>
        )}
      </div>

      {/* Table des demandes */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="w-5 h-5 border-2 border-mcdm-third-300 border-t-mcdm-third-500 rounded-full animate-spin" />
        </div>
      ) : requests.length === 0 ? (
        <p className="text-sm text-mcdm-third-300 dark:text-mcdm-primary-300 italic py-2">
          Aucune demande d'accès pour le moment.
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-mcdm-primary-200 dark:border-mcdm-third-300">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-mcdm-primary-200/50 dark:bg-mcdm-third-300/50">
                <th className="text-left px-4 py-2.5 font-medium text-mcdm-third-400 dark:text-mcdm-primary-200">
                  Utilisateur
                </th>
                <th className="text-left px-4 py-2.5 font-medium text-mcdm-third-400 dark:text-mcdm-primary-200">
                  Statut
                </th>
                <th className="text-left px-4 py-2.5 font-medium text-mcdm-third-400 dark:text-mcdm-primary-200">
                  Date
                </th>
                <th className="text-right px-4 py-2.5 font-medium text-mcdm-third-400 dark:text-mcdm-primary-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-mcdm-primary-200 dark:divide-mcdm-third-300">
              {requests.map((req) => {
                const config = statusConfig[req.status];
                const requester = req.requesterId;

                return (
                  <tr
                    key={req._id}
                    className="hover:bg-mcdm-primary-200/30 dark:hover:bg-mcdm-third-300/30 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-mcdm-third-500 dark:text-mcdm-primary-100">
                          {requester.firstName} {requester.lastName}
                        </p>
                        <p className="text-xs text-mcdm-third-300 dark:text-mcdm-primary-300">
                          {requester.email}
                        </p>
                        {req.message && (
                          <p className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-200 mt-1 italic">
                            &ldquo;{req.message}&rdquo;
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
                        {config.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-mcdm-third-300 dark:text-mcdm-primary-300 text-xs">
                      {new Date(req.requestedAt).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {req.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleRespond(req._id, 'approve')}
                              className="p-1.5 rounded-md bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50 transition-colors"
                              title="Accepter"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleRespond(req._id, 'reject')}
                              className="p-1.5 rounded-md bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 transition-colors"
                              title="Refuser"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </>
                        )}
                        {req.status === 'approved' && (
                          <button
                            onClick={() => handleRevoke(req._id)}
                            className="px-3 py-1 rounded-md text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                          >
                            Révoquer
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AccessRequestsTable;
